import { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { Modal, useToast } from './ui/ToastModal';
import { usePnlCategories } from '../hooks/usePnlCategories';
import type { PnlCategory } from '../hooks/usePnlCategories';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabase';
import { writeRenameAliases } from '../features/pnlAnalytics/aliasWrite';
import { detectCategoryRenames } from '../features/pnlAnalytics/renameDetection';
import { makeCustomMetricKey } from '../lib/pnl/customMetricKey';
import { canDeleteOperationalMetric } from '../features/pnlAnalytics/operationalMetricDeletion';
import { canRemovePnlCategory, type OccupiedProjectMonth } from '../features/pnlAnalytics/categoryRemoval';
import {
  carryRenamedCategoryValues,
  renameCarryFailureMessage,
  type CategoryValueClient,
  type CategoryValueFilter,
} from '../features/pnlAnalytics/categoryRenameValues';
import {
  carryRenamedCategoryOverlays,
  overlayCarryFailureMessage,
  type OverlayConfigClient,
} from '../features/pnlAnalytics/projectPnlViewRename';
import { claimTimingDb } from '../lib/claimTimingDb';
import { looksLikeQuantityName, QUANTITY_NAME_HINT } from '../features/pnlAnalytics/currencyCategoryNaming';
import { categoryEntersFinancialPnl } from '../lib/pnl/pnlCalculationCore';
import { createPortal } from 'react-dom';
import { Plus, Trash2, GripVertical, Save, Loader2, Info, X, AlertTriangle, Clock, Hash, Percent as PercentIcon } from 'lucide-react';

/* ─── SEQ-182 T14: operational (non-currency) metrics ─────────────────────────
 * Ticket "Category Configuration": Value Type (Currency, Number) and, for
 * Number, a Unit (Hours, Count, Percent). Currency stays exactly the
 * existing category flow below (name + group + deduct, saved into
 * app_settings.pnl_expense_categories). A non-currency metric is a
 * different kind of object, not a category: it is never added to that list
 * (a non-currency string in it would be summed as a dollar deduction by the
 * V1 P&L engine, pnlCalculationCore.ts), so it gets its own row in
 * pnl_metric_registry instead, written with financial_inclusion = false.
 * This section handles creation only; editing an existing currency category
 * into a non-currency one is a bigger, destructive migration this ticket
 * does not ask for. */

/* SEQ-180 phase 2, P24: Percent is offered again.
 *
 * SEQ-182 withdrew it because every metric created here was inserted with
 * aggregation_method: 'sum', and a summed percent (twelve monthly "12.5%"
 * rows added into a Total column) is exactly the AC10 mistake typed metrics
 * exist to prevent. Two things changed, and Percent only returns because
 * both did:
 *
 *   1. The registry CHECK admits 'avg' again, with a new invariant that a
 *      ratio-unit metric can NEVER be 'sum' (D10), so the rule is enforced
 *      by the schema rather than by this picker remembering to be careful.
 *      See supabase/migrations/20260910000050_pnl_registry_org_row_guard.sql.
 *   2. A value entry path exists (D26): pnl_manual_metric_value, edited on
 *      the project P&L tab's operational band, where an 'avg' metric's Total
 *      column is the mean of the months carrying a value, never their sum
 *      (D27, manualMetricTotals.ts).
 *
 * `aggregationFor` below is the single place a unit picks its method, so a
 * new unit cannot be added without answering the question.
 */
type OperationalUnit = 'hours' | 'count' | 'percent';

const OPERATIONAL_UNIT_CONFIG: Record<OperationalUnit, { label: string; icon: typeof Clock; decimals: number }> = {
  hours: { label: 'Hours', icon: Clock, decimals: 1 },
  count: { label: 'Count', icon: Hash, decimals: 0 },
  percent: { label: 'Percent', icon: PercentIcon, decimals: 2 },
};

/**
 * Hours and counts are additive, so they sum. A percent is an observation,
 * not a quantity, so it averages: D10's CHECK refuses `sum` on a ratio unit
 * outright, which means a wrong answer here fails the insert rather than
 * quietly producing a nonsense Total column.
 */
function aggregationFor(unit: OperationalUnit): 'sum' | 'avg' {
  return unit === 'percent' ? 'avg' : 'sum';
}

/**
 * A registry row already in the database can carry any unit string,
 * including 'percent' from before this fix (or any future unit), so this
 * is intentionally wider than `OperationalUnit`, which is only the
 * picker's own creation vocabulary. `operationalUnitDisplay` below never
 * assumes a fetched row's unit is one this modal still offers to create.
 */
interface OperationalMetricRow {
  metric_key: string;
  display_name: string;
  unit: string;
  section: 'labour' | 'expense';
  /** Null on a system row. Read so the delete guard can refuse a built-in key without a round trip. */
  organization_id: string | null;
  metric_role: string;
  kind: string;
}

/** Looks up display config for a fetched row's unit, falling back to the raw unit string for anything this picker does not create (e.g. a 'ratio' row written by a migration), so a chip always renders instead of crashing on an unrecognised unit. */
function operationalUnitDisplay(unit: string): { label: string; icon: typeof Clock } {
  const known = OPERATIONAL_UNIT_CONFIG[unit as OperationalUnit];
  if (known) return known;
  return { label: unit, icon: Hash };
}

/* ─── Group visual config ─────────────────────────────────────────────────── */

const GROUP_ORDER = ['Wages', 'Materials', 'Other'] as const;

const GROUP_STYLE: Record<string, { emoji: string; bg: string; color: string; border: string; headerBg: string; desc: string }> = {
  Wages:     { emoji: '👷', bg: '#fffbeb', color: '#92400e', border: 'rgba(245,158,11,0.25)', headerBg: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)', desc: 'Labor, contractors & worker costs' },
  Materials: { emoji: '📦', bg: '#f5f3ff', color: '#5b21b6', border: 'rgba(109,40,217,0.2)', headerBg: 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)', desc: 'Physical materials & supplies' },
  Other:     { emoji: '⚙️', bg: '#f0f7ff', color: '#1a5a9a', border: 'rgba(30,115,190,0.2)', headerBg: 'linear-gradient(135deg, #f0f7ff 0%, #e8f1fc 100%)', desc: 'Overhead, office & miscellaneous' },
};

/* ─── Supabase adapter for the rename value carry ─────────────────────────────
 * categoryRenameValues.ts describes the client structurally rather than
 * importing SupabaseClient, so that Deno can type-check and test it without a
 * populated node_modules. Handing it the real client directly does not
 * compile: the generated Supabase types make from().update().eq().select() a
 * deeply generic chain and TypeScript gives up on matching it against the
 * small interface (TS2589, "type instantiation is excessively deep").
 *
 * This pins that chain to the one call the feature makes. The `any` is the
 * same one every untyped-table call in this file already carries: neither
 * claims_pnl_monthly_values' relations nor the P&L tables are in the
 * generated types yet.
 */
function pnlValueClient(): CategoryValueClient {
  /* eslint-disable @typescript-eslint/no-explicit-any -- see the note above: the generated chain cannot be matched structurally */
  const wrap = (builder: any): CategoryValueFilter => ({
    eq: (column: string, value: string) => wrap(builder.eq(column, value)),
    select: (columns: string) => builder.select(columns),
  });
  return {
    from: (table: string) => ({
      update: (values: { category: string }) => wrap((supabase as any).from(table).update(values)),
    }),
  };
  /* eslint-enable @typescript-eslint/no-explicit-any */
}

/* ─── Supabase adapter for the rename overlay carry ───────────────────────────
 * The same shape as the adapter above and for the same reason, with one
 * difference: claims_project_config is not in the generated types at all, so
 * this goes through claimTimingDb, the client SEQ-169 typed by hand. That is
 * also why no `any` is needed here: that schema type is small enough for
 * TypeScript to match structurally.
 *
 * The column name is passed through rather than assumed, so that the two calls
 * the module makes (scoped to the organisation on the read, to one project on
 * the write) stay the module's decision and not this adapter's.
 */
function pnlOverlayClient(): OverlayConfigClient {
  return {
    from: () => ({
      select: () => ({
        eq: (column: string, value: string) => {
          const rows = claimTimingDb.from('claims_project_config').select('project_id, config');
          return column === 'organization_id'
            ? rows.eq('organization_id', value)
            : rows.eq('project_id', value);
        },
      }),
      update: (values: { config: Record<string, unknown> }) => ({
        eq: (column: string, value: string) => ({
          select: () => {
            const write = claimTimingDb.from('claims_project_config').update(values);
            return column === 'project_id'
              ? write.eq('project_id', value).select('project_id')
              : write.eq('organization_id', value).select('project_id');
          },
        }),
      }),
    }),
  };
}

/* ─── Props ────────────────────────────────────────────────────────────────── */

interface EditExpenseCategoriesModalProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Called after an operational metric is added or deleted. The project P&L
   * tab's operational band reads the same registry, and it is mounted behind
   * this dialog, so without this a deleted metric's row stays on screen until
   * a reload.
   */
  onOperationalMetricsChanged?: () => void;
}

/* ─── Component ────────────────────────────────────────────────────────────── */

export function EditExpenseCategoriesModal({ isOpen, onClose, onOperationalMetricsChanged }: EditExpenseCategoriesModalProps) {
  const { categories, loading, updateCategories } = usePnlCategories();
  const { organizationId, isAdmin } = useAuth();
  const [localCategories, setLocalCategories] = useState<PnlCategory[]>(() =>
    categories.map(c => ({ ...c }))
  );
  // usePnlCategories() seeds `categories` with DEFAULT_CATEGORIES / [] and
  // resolves the real list asynchronously (its own fetch effect). The
  // useState initializer above only runs once, at this render, so without
  // this it captures whatever `categories` was before that fetch resolved
  // and never picks up the loaded data. `dirty` stops this effect from
  // clobbering an edit already in progress if `categories` changes again
  // mid-session (e.g. a slow fetch resolving after the user started typing).
  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    if (!isOpen || dirty) return;
    function syncFromFetch() {
      setLocalCategories(categories.map(c => ({ ...c })));
    }
    syncFromFetch();
  }, [categories, isOpen, dirty]);
  // Every open remounts this component (ProjectStatusTab keys it by
  // showEditCategories), so this mainly guards a future integration that
  // does not remount: a fresh open should always start clean.
  useEffect(() => {
    function resetDirty() {
      setDirty(false);
    }
    resetDirty();
  }, [isOpen]);

  const [isSaving, setIsSaving] = useState(false);
  const [showGuide, setShowGuide] = useState(() => {
    try { return localStorage.getItem('pnl-cat-guide-hide') !== '1'; } catch { return true; }
  });
  const { addToast } = useToast();
  const listEndRef = useRef<HTMLDivElement>(null);

  // SEQ-182 T14: operational (non-currency) metrics, loaded and written
  // straight to pnl_metric_registry, entirely separate from localCategories
  // / updateCategories above so a non-currency metric can never end up in
  // app_settings.pnl_expense_categories.
  const [operationalMetrics, setOperationalMetrics] = useState<OperationalMetricRow[]>([]);
  const [loadingOperational, setLoadingOperational] = useState(false);
  const [newOpName, setNewOpName] = useState('');
  const [newOpUnit, setNewOpUnit] = useState<OperationalUnit>('hours');
  const [savingOperational, setSavingOperational] = useState(false);

  useEffect(() => {
    if (!isOpen || !organizationId) return;
    let cancelled = false;
    setLoadingOperational(true);
    /* eslint-disable @typescript-eslint/no-explicit-any -- pnl_metric_registry is not yet in generated Supabase types (SEQ-180/182) */
    (supabase as any)
      .from('pnl_metric_registry')
      .select('metric_key, display_name, unit, section, organization_id, metric_role, kind')
      .eq('organization_id', organizationId)
      .eq('metric_role', 'operational')
      .eq('kind', 'input')
      .like('metric_key', 'operational.custom.%')
      .order('display_name', { ascending: true })
      .then(({ data, error }: { data: OperationalMetricRow[] | null; error: { message: string } | null }) => {
        /* eslint-enable @typescript-eslint/no-explicit-any */
        if (cancelled) return;
        if (error) {
          addToast({ type: 'error', title: 'Could not load operational metrics', message: error.message });
        } else {
          setOperationalMetrics(data ?? []);
        }
        setLoadingOperational(false);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- addToast is stable from context; re-fetch only on open/org change
  }, [isOpen, organizationId]);

  const handleAddOperationalMetric = useCallback(async () => {
    const name = newOpName.trim();
    if (!name) {
      addToast({ type: 'error', title: 'Name required', message: 'Give the operational metric a name.' });
      return;
    }
    if (!organizationId) {
      addToast({ type: 'error', title: 'No organisation', message: 'Sign in to an organisation to add operational metrics.' });
      return;
    }

    setSavingOperational(true);
    // Design audit, MEDIUM: the group picker promised a placement this
    // feature does not deliver. An operational metric always renders in the
    // operational band under Wages (PnlManualMetricRows), whatever section
    // the registry row named, so offering Materials or Other told the user
    // they could put it somewhere it would never appear. The picker is gone
    // and the section is what the band actually reads.
    const section = 'labour' as const;
    const metricKey = makeCustomMetricKey('operational');
    const { decimals } = OPERATIONAL_UNIT_CONFIG[newOpUnit];

    /* eslint-disable @typescript-eslint/no-explicit-any -- pnl_metric_registry is not yet in generated Supabase types (SEQ-180/182) */
    const { data: maxRow } = await (supabase as any)
      .from('pnl_metric_registry')
      .select('sort_order')
      .eq('organization_id', organizationId)
      .order('sort_order', { ascending: false })
      .limit(1)
      .maybeSingle();
    const sortOrder = (maxRow?.sort_order ?? 0) + 1;

    const { error } = await (supabase as any).from('pnl_metric_registry').insert({
      metric_key: metricKey,
      organization_id: organizationId,
      section,
      display_name: name,
      kind: 'input',
      unit: newOpUnit,
      sort_order: sortOrder,
      is_deductible: false,
      metric_role: 'operational',
      financial_inclusion: false,
      aggregation_method: aggregationFor(newOpUnit),
      decimals,
    });
    /* eslint-enable @typescript-eslint/no-explicit-any */

    setSavingOperational(false);

    if (error) {
      addToast({ type: 'error', title: 'Could not add operational metric', message: error.message });
      return;
    }

    setOperationalMetrics(prev =>
      [
        ...prev,
        {
          metric_key: metricKey,
          display_name: name,
          unit: newOpUnit,
          section,
          organization_id: organizationId,
          metric_role: 'operational',
          kind: 'input',
        },
      ].sort((a, b) => a.display_name.localeCompare(b.display_name)),
    );
    setNewOpName('');
    onOperationalMetricsChanged?.();
    addToast({ type: 'success', title: 'Operational metric added', message: `"${name}" is now available in P&L analytics.` });
  }, [newOpName, newOpUnit, organizationId, addToast, onOperationalMetricsChanged]);

  /* ── Delete an operational metric ─────────────────────────────────────────
   * The affordance the walkthrough found missing: a metric created with a
   * typo was permanent. It is a guarded delete rather than a plain one
   * because pnl_manual_metric_value.metric_key cascades on delete, so
   * removing the registry row would take every value anyone typed under it
   * with no error and nothing on screen. `canDeleteOperationalMetric` owns
   * every rule; this only counts, asks and writes. */
  const [pendingDelete, setPendingDelete] = useState<OperationalMetricRow | null>(null);
  const [deleteRefusal, setDeleteRefusal] = useState<{ title: string; message: string } | null>(null);
  // A set, not one key (React review round 3, LOW). With a single value,
  // clicking delete on a second chip while the first chip's count check was
  // still in flight re-enabled the first button, so a rapid double-click
  // could start a second concurrent check for it.
  //
  // The ref is the guard and the state is only for rendering. A state
  // updater must be a pure function of its argument, so it cannot also
  // report "this one was already running"; React is free to call it twice.
  const inFlightDeleteChecks = useRef<Set<string>>(new Set());
  const [checkingDelete, setCheckingDelete] = useState<ReadonlySet<string>>(() => new Set());
  const [deletingMetric, setDeletingMetric] = useState(false);

  const handleRequestDeleteOperational = useCallback(async (metric: OperationalMetricRow) => {
    if (inFlightDeleteChecks.current.has(metric.metric_key)) return;
    inFlightDeleteChecks.current.add(metric.metric_key);
    setCheckingDelete(prev => new Set(prev).add(metric.metric_key));

    // Counted with head: true, so the row bodies never cross the wire; only
    // the count matters here. An error is treated as "cannot prove it is
    // empty", which must refuse rather than fall through to a delete.
    /* eslint-disable @typescript-eslint/no-explicit-any -- pnl_manual_metric_value is not in the generated Supabase types (SEQ-180 phase 2) */
    const { count, error: countError } = await (supabase as any)
      .from('pnl_manual_metric_value')
      .select('id', { count: 'exact', head: true })
      .eq('organization_id', organizationId)
      .eq('metric_key', metric.metric_key);
    /* eslint-enable @typescript-eslint/no-explicit-any */

    inFlightDeleteChecks.current.delete(metric.metric_key);
    setCheckingDelete(prev => {
      const next = new Set(prev);
      next.delete(metric.metric_key);
      return next;
    });

    if (countError) {
      setDeleteRefusal({
        title: 'Could not check for typed values',
        message: `${countError.message} Deleting would remove any values typed under this metric, so nothing has been deleted. Try again in a moment.`,
      });
      return;
    }

    const verdict = canDeleteOperationalMetric({
      metricKey: metric.metric_key,
      displayName: metric.display_name,
      rowOrganizationId: metric.organization_id,
      metricRole: metric.metric_role,
      kind: metric.kind,
      viewerOrganizationId: organizationId,
      viewerIsOrgAdmin: isAdmin,
      valueCount: count ?? 0,
    });

    if (!verdict.allowed) {
      setDeleteRefusal({ title: verdict.title, message: verdict.message });
      return;
    }
    setPendingDelete(metric);
  }, [organizationId, isAdmin]);

  const handleConfirmDeleteOperational = useCallback(async () => {
    if (!pendingDelete) return;
    setDeletingMetric(true);

    /* eslint-disable @typescript-eslint/no-explicit-any -- pnl_metric_registry is not in the generated Supabase types (SEQ-180/182) */
    const { data, error } = await (supabase as any)
      .from('pnl_metric_registry')
      .delete()
      .eq('organization_id', organizationId)
      .eq('metric_key', pendingDelete.metric_key)
      .select('metric_key');
    /* eslint-enable @typescript-eslint/no-explicit-any */

    setDeletingMetric(false);

    if (error) {
      addToast({ type: 'error', title: 'Could not delete metric', message: error.message });
      return;
    }
    // Row count as well as the error: a delete refused by RLS returns
    // neither, which is the silent no-op this codebase has been bitten by.
    if (!data || data.length === 0) {
      addToast({
        type: 'error',
        title: 'Metric not deleted',
        message: 'Nothing was deleted. You may not have permission to change the metrics for this organisation.',
      });
      return;
    }

    const deletedName = pendingDelete.display_name;
    setOperationalMetrics(prev => prev.filter(m => m.metric_key !== pendingDelete.metric_key));
    setPendingDelete(null);
    onOperationalMetricsChanged?.();
    addToast({ type: 'success', title: 'Operational metric deleted', message: `"${deletedName}" has been removed.` });
  }, [pendingDelete, organizationId, addToast, onOperationalMetricsChanged]);

  const handleChange = useCallback(
    (idx: number, key: keyof PnlCategory, value: string | boolean) => {
      setDirty(true);
      setLocalCategories(prev => {
        const next = [...prev];
        next[idx] = { ...next[idx], [key]: value };
        return next;
      });
    }, []
  );

  /* ── Remove a currency category, guarded ─────────────────────────
   * Removing a category used to be a pure local filter, which is how two
   * categories were removed on 8 September 2026 while their values stayed in
   * claims_pnl_monthly_values with nothing left to show them. The count runs
   * before the row leaves the list, not on save, so the user is told at the
   * moment they ask rather than after typing five other edits.
   * `canRemovePnlCategory` owns every rule; this only counts, asks and
   * removes. */
  const inFlightRemovalChecks = useRef<Set<string>>(new Set());
  const [checkingRemoval, setCheckingRemoval] = useState<ReadonlySet<string>>(() => new Set());

  const handleRemove = useCallback(async (idx: number) => {
    const category = localCategories[idx];
    if (!category) return;
    const name = category.name.trim();

    // An unsaved, unnamed row cannot have values under it: nothing has ever
    // been written with that string. Removing it is a pure undo of the Add
    // button and must not cost a round trip.
    if (name.length === 0) {
      setDirty(true);
      setLocalCategories(prev => prev.filter((_, i) => i !== idx));
      return;
    }

    // No organisation in context means the count below would filter on null
    // and come back empty, which would read as "no values" and allow the
    // removal. Treated as a failed count, which refuses.
    if (!organizationId) {
      const verdict = canRemovePnlCategory({ categoryName: name, occupied: [], viewerIsOrgAdmin: isAdmin, countFailed: true });
      if (!verdict.allowed) setDeleteRefusal({ title: verdict.title, message: verdict.message });
      return;
    }

    if (inFlightRemovalChecks.current.has(name)) return;
    inFlightRemovalChecks.current.add(name);
    setCheckingRemoval(prev => new Set(prev).add(name));

    /* eslint-disable @typescript-eslint/no-explicit-any -- claims_pnl_monthly_values' embedded claims_projects relation is not in the generated Supabase types */
    const { data, error } = await (supabase as any)
      .from('claims_pnl_monthly_values')
      .select('month_date, amount, claims_projects(job_location)')
      .eq('organization_id', organizationId)
      .eq('category', name)
      .order('month_date', { ascending: true }) as {
        data: { month_date: string; amount: number | string; claims_projects: { job_location: string } | null }[] | null;
        error: { message: string } | null;
      };
    /* eslint-enable @typescript-eslint/no-explicit-any */

    inFlightRemovalChecks.current.delete(name);
    setCheckingRemoval(prev => {
      const next = new Set(prev);
      next.delete(name);
      return next;
    });

    const occupied: OccupiedProjectMonth[] = (data ?? []).map(row => ({
      projectName: row.claims_projects?.job_location ?? 'an unnamed project',
      monthDate: row.month_date,
      amount: Number(row.amount) || 0,
    }));

    const verdict = canRemovePnlCategory({
      categoryName: name,
      occupied,
      viewerIsOrgAdmin: isAdmin,
      countFailed: Boolean(error),
    });

    if (!verdict.allowed) {
      setDeleteRefusal({ title: verdict.title, message: verdict.message });
      return;
    }

    setDirty(true);
    setLocalCategories(prev => prev.filter((_, i) => i !== idx));
  }, [localCategories, organizationId, isAdmin]);

  const [newlyAddedIdx, setNewlyAddedIdx] = useState<number | null>(null);

  const handleAdd = useCallback((group: string = 'Other') => {
    setDirty(true);
    setLocalCategories(prev => {
      const newIdx = prev.length;
      // Schedule focus + scroll after React renders the new item
      setTimeout(() => setNewlyAddedIdx(newIdx), 0);
      return [...prev, { name: '', is_profit_deduction: true, group: group as PnlCategory['group'], financial_inclusion: true }];
    });
  }, []);

  const dismissGuide = useCallback(() => {
    setShowGuide(false);
    try { localStorage.setItem('pnl-cat-guide-hide', '1'); } catch { /* noop */ }
  }, []);

  /* Duplicate detection */
  const nameCount = useMemo(() => {
    const map = new Map<string, number>();
    for (const c of localCategories) {
      const n = c.name.trim().toLowerCase();
      if (n) map.set(n, (map.get(n) || 0) + 1);
    }
    return map;
  }, [localCategories]);

  const isDup = (name: string) => (nameCount.get(name.trim().toLowerCase()) || 0) > 1;
  const hasDuplicates = [...nameCount.values()].some(c => c > 1);

  /* Group categories: preserves original order within each group */
  const grouped = useMemo(() => {
    const result: Record<string, { cat: PnlCategory; originalIdx: number }[]> = {
      Wages: [], Materials: [], Other: [],
    };
    localCategories.forEach((cat, idx) => {
      const g = cat.group in result ? cat.group : 'Other';
      result[g].push({ cat, originalIdx: idx });
    });
    return result;
  }, [localCategories]);

  const hasChanges = JSON.stringify(localCategories) !== JSON.stringify(categories);
  const hasEmpty = localCategories.some(c => c.name.trim().length === 0);

  const handleSave = async () => {
    // Re-order: save grouped (Wages → Materials → Other)
    const ordered: PnlCategory[] = [];
    for (const g of GROUP_ORDER) {
      for (const { cat } of grouped[g]) {
        if (cat.name.trim().length > 0) {
          ordered.push({ ...cat, name: cat.name.trim() });
        }
      }
    }

    if (ordered.length === 0) {
      addToast({ type: 'error', title: 'No Categories', message: 'Add at least one category.' });
      return;
    }
    const uniqueNames = new Set(ordered.map(c => c.name.toLowerCase()));
    if (uniqueNames.size !== ordered.length) {
      addToast({ type: 'error', title: 'Duplicate Names', message: 'Each category must have a unique name.' });
      return;
    }

    setIsSaving(true);

    // SEQ-180 E20: detect renames before the save re-groups everything.
    // detectCategoryRenames only calls something a rename when the list
    // length is unchanged and no other field on that entry changed either,
    // so a delete that shifts later entries down an index never mis-aliases
    // them (round 2 code review, critical: see renameDetection.ts).
    const renames = detectCategoryRenames(categories, localCategories);

    const success = await updateCategories(ordered);

    if (success && renames.length > 0 && organizationId) {
      const { error } = await writeRenameAliases(supabase, organizationId, renames);
      if (error) {
        // The P&L category save already succeeded (existing behaviour is
        // unchanged); this only warns that the analytics side alias did not
        // follow, so a renamed category may show as "unmapped" on the
        // P&L analytics page until re-saved or fixed on the admin screen.
        addToast({ type: 'error', title: 'Analytics alias not saved', message: error });
      }

      // The alias above keeps the ANALYTICS side resolving; this moves the
      // data itself, which is what the project P&L tab and the company
      // dashboard read. Without it a rename leaves every existing row under
      // the old string: invisible on the tab, still counted on the
      // dashboard, which is the same orphan state the removal guard exists
      // to prevent. See features/pnlAnalytics/categoryRenameValues.ts.
      const carry = await carryRenamedCategoryValues(pnlValueClient(), organizationId, renames);
      if (carry.error) {
        addToast({ type: 'error', title: 'Typed values not moved', message: renameCarryFailureMessage(carry.error) });
      } else if (carry.movedRowCount > 0) {
        addToast({
          type: 'success',
          title: 'Values moved',
          message: `${carry.movedRowCount} typed ${carry.movedRowCount === 1 ? 'value' : 'values'} moved to the new category name.`,
        });
      }

      // The third thing a rename has to carry: each project's own view of this
      // line. The per-project P&L overlay keys on the category NAME, so without
      // this the line comes back on every project that had hidden, reordered or
      // relabelled it, unhidden and under the new word, which nobody asked for
      // and nobody is told about. See features/pnlAnalytics/projectPnlViewRename.ts.
      const overlays = await carryRenamedCategoryOverlays(pnlOverlayClient(), organizationId, renames);
      if (overlays.error) {
        addToast({ type: 'error', title: 'Project P&L layouts not moved', message: overlayCarryFailureMessage(overlays.error) });
      } else if (overlays.changedProjectCount > 0) {
        addToast({
          type: 'success',
          title: 'Project layouts moved',
          message: `${overlays.changedProjectCount} ${overlays.changedProjectCount === 1 ? 'project keeps its' : 'projects keep their'} P&L layout for the renamed category.`,
        });
      }
    }

    setIsSaving(false);
    if (success) onClose();
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Edit P&L Categories" size="lg" hideFooter>
      {/* 
        Outer flex column fills the modal body.
        The scrollable area is the middle section only.
        Guide + footer stay pinned.
      */}
      <div style={{
        display: 'flex', flexDirection: 'column',
        height: 'calc(80vh - 140px)', /* modal header ~60px + body padding 48px + buffer */
        margin: '-24px', /* cancel out Modal body padding so we control it */
      }}>

        {/* ── Fixed Top: Guide ─────────────────────────────────────── */}
        <div style={{ padding: '16px 20px 0 20px', flexShrink: 0 }}>
          {showGuide && (
            <div style={{
              display: 'flex', gap: 12, position: 'relative',
              background: 'linear-gradient(135deg, #f0f7ff 0%, #e8f1fc 100%)',
              border: '1px solid rgba(30,115,190,0.15)',
              borderRadius: 10, padding: '12px 36px 12px 14px',
              marginBottom: 12,
            }}>
              <div style={{
                width: 26, height: 26, borderRadius: 7,
                background: '#1e73be', color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <Info size={14} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 12, color: '#0f172a', marginBottom: 4 }}>Quick Guide</div>
                <div style={{ fontSize: 11.5, lineHeight: 1.6, color: '#475569' }}>
                  Every category here is a <strong style={{ color: '#1a5a9a' }}>dollar amount</strong>. Hours, counts and
                  percentages belong under Operational metrics at the bottom of this list.
                  <br />
                  <strong style={{ color: '#1a5a9a' }}>Category Name</strong>: Row label in your P&L table &nbsp;·&nbsp;
                  <strong style={{ color: '#1a5a9a' }}>Group</strong>: Organizes into sections &nbsp;·&nbsp;
                  <strong style={{ color: '#1a5a9a' }}>In P&L</strong>: Counts towards totals, percentages and net profit &nbsp;·&nbsp;
                  <strong style={{ color: '#1a5a9a' }}>Deduct</strong>: Subtract from income for Net Profit
                </div>
              </div>
              <button onClick={dismissGuide} style={{
                position: 'absolute', top: 6, right: 6,
                background: 'none', border: 'none', cursor: 'pointer',
                padding: 4, borderRadius: 6, color: '#94a3b8',
              }} aria-label="Dismiss guide">
                <X size={13} />
              </button>
            </div>
          )}
        </div>

        {/* ── Scrollable Middle: Grouped Categories ────────────────── */}
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
            <Loader2 size={28} className="spinner" color="var(--color-primary)" />
          </div>
        ) : (
          <div style={{
            flex: 1, overflowY: 'auto', minHeight: 0,
            padding: '0 20px',
          }}>
            {GROUP_ORDER.map(group => {
              const items = grouped[group];
              const gs = GROUP_STYLE[group];
              if (items.length === 0) return null;

              return (
                <div key={group} style={{ marginBottom: 16 }}>
                  {/* Group Header */}
                  <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '8px 12px', borderRadius: 8,
                    background: gs.headerBg,
                    border: `1px solid ${gs.border}`,
                    marginBottom: 6,
                    position: 'sticky', top: 0, zIndex: 2,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 15 }}>{gs.emoji}</span>
                      <span style={{ fontWeight: 700, fontSize: 13, color: gs.color }}>{group}</span>
                      <span style={{ fontSize: 11, color: gs.color, opacity: 0.7 }}>&middot; {gs.desc}</span>
                    </div>
                    <span style={{
                      fontSize: 10.5, fontWeight: 700, color: gs.color,
                      background: 'rgba(255,255,255,0.6)', padding: '2px 8px',
                      borderRadius: 10,
                    }}>
                      {items.length}
                    </span>
                  </div>

                  {/* Category Cards */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {items.map(({ cat, originalIdx }) => {
                      const duplicate = isDup(cat.name);
                      const inFinancialPnl = categoryEntersFinancialPnl(cat);
                      const quantityName = looksLikeQuantityName(cat.name);
                      return (
                        <div
                          key={originalIdx}
                          style={{
                            display: 'flex', flexDirection: 'column', gap: 6,
                            padding: '8px 10px',
                            border: `1px solid ${duplicate ? '#fca5a5' : 'var(--border-color)'}`,
                            borderRadius: 8,
                            background: duplicate ? '#fef2f2' : 'var(--bg-secondary)',
                            transition: 'border-color 0.15s, box-shadow 0.15s',
                          }}
                        >
                         <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <GripVertical size={13} color="var(--text-muted)" style={{ cursor: 'grab', flexShrink: 0, opacity: 0.35 }} />

                          {/* Name Input */}
                          <input
                            type="text"
                            className="form-input"
                            value={cat.name}
                            onChange={e => handleChange(originalIdx, 'name', e.target.value)}
                            placeholder="Category name…"
                            style={{ flex: 1, margin: 0, minWidth: 0, padding: '6px 10px', fontSize: 13 }}
                            ref={el => {
                              if (el && newlyAddedIdx === originalIdx) {
                                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                // Small delay so scroll completes before focus
                                setTimeout(() => el.focus(), 120);
                                setNewlyAddedIdx(null);
                              }
                            }}
                          />

                          {/* Group Select: compact */}
                          <select
                            className="form-input"
                            value={cat.group}
                            onChange={e => handleChange(originalIdx, 'group', e.target.value)}
                            style={{
                              margin: 0, padding: '5px 6px', fontSize: 11.5,
                              width: 'auto', minWidth: 90, fontWeight: 600,
                              background: gs.bg, color: gs.color,
                              borderColor: gs.border,
                            }}
                          >
                            <option value="Wages">Wages</option>
                            <option value="Materials">Materials</option>
                            <option value="Other">Other</option>
                          </select>

                          {/* SEQ-182 "Include in Financial P&L". Sits before
                              Deduct because it is the wider question: an
                              excluded category is shown on the P&L and enters
                              no total, no ratio and no net profit, which makes
                              Deduct meaningless, so Deduct is disabled rather
                              than left looking as if it still decides
                              something. */}
                          <label
                            style={{
                              display: 'flex', alignItems: 'center', gap: 5,
                              cursor: 'pointer', userSelect: 'none', flexShrink: 0,
                            }}
                            title="Off: this row still shows on the P&L, but its values enter no total, no percentage and no net profit."
                          >
                            <input
                              type="checkbox"
                              checked={inFinancialPnl}
                              onChange={e => handleChange(originalIdx, 'financial_inclusion', e.target.checked)}
                              style={{ width: 14, height: 14, accentColor: '#1e73be', cursor: 'pointer' }}
                            />
                            <span style={{
                              fontSize: 11, fontWeight: 500, whiteSpace: 'nowrap',
                              color: inFinancialPnl ? '#1e73be' : '#94a3b8',
                            }}>
                              In P&amp;L
                            </span>
                          </label>

                          {/* Deduct Checkbox */}
                          <label style={{
                            display: 'flex', alignItems: 'center', gap: 5,
                            cursor: inFinancialPnl ? 'pointer' : 'not-allowed', userSelect: 'none', flexShrink: 0,
                            opacity: inFinancialPnl ? 1 : 0.45,
                          }}>
                            <input
                              type="checkbox"
                              checked={cat.is_profit_deduction}
                              disabled={!inFinancialPnl}
                              onChange={e => handleChange(originalIdx, 'is_profit_deduction', e.target.checked)}
                              style={{ width: 14, height: 14, accentColor: '#1e73be', cursor: inFinancialPnl ? 'pointer' : 'not-allowed' }}
                            />
                            <span style={{
                              fontSize: 11, fontWeight: 500, whiteSpace: 'nowrap',
                              color: cat.is_profit_deduction && inFinancialPnl ? '#1e73be' : '#94a3b8',
                            }}>
                              Deduct
                            </span>
                          </label>

                          {/* Delete */}
                          <button
                            className="btn btn-ghost"
                            onClick={() => { void handleRemove(originalIdx); }}
                            disabled={checkingRemoval.has(cat.name.trim())}
                            style={{ padding: 5, color: '#cbd5e1', flexShrink: 0 }}
                            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = '#ef4444'; }}
                            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = '#cbd5e1'; }}
                            aria-label={`Remove category ${cat.name.trim() || '(unnamed)'}`}
                          >
                            {checkingRemoval.has(cat.name.trim()) ? <Loader2 size={13} className="spinner" /> : <Trash2 size={13} />}
                          </button>
                         </div>

                          {quantityName && (
                            <div style={{
                              display: 'flex', gap: 6, alignItems: 'flex-start',
                              margin: '0 0 0 21px', padding: '6px 9px',
                              background: '#fffbeb', border: '1px solid rgba(245,158,11,0.25)',
                              borderRadius: 6, fontSize: 11, lineHeight: 1.5, color: '#92400e',
                            }}>
                              <AlertTriangle size={12} style={{ flexShrink: 0, marginTop: 2 }} />
                              <span>{QUANTITY_NAME_HINT}</span>
                            </div>
                          )}

                          {!inFinancialPnl && (
                            <div style={{
                              margin: '0 0 0 21px', fontSize: 11, lineHeight: 1.5, color: '#64748b',
                            }}>
                              Shown on the P&amp;L, counted in nothing: this category enters no total, no percentage and no net profit.
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}

            {/* Show empty groups hint */}
            {GROUP_ORDER.filter(g => grouped[g].length === 0).length > 0 && (
              <div style={{
                textAlign: 'center', padding: '8px 0 4px 0',
                fontSize: 11, color: '#94a3b8',
              }}>
                {GROUP_ORDER.filter(g => grouped[g].length === 0).map(g => (
                  <span key={g} style={{ marginRight: 12 }}>
                    {GROUP_STYLE[g].emoji} No {g.toLowerCase()} categories yet
                  </span>
                ))}
              </div>
            )}
            {/* ── SEQ-182 T14: operational (non-currency) metrics ─────── */}
            <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px dashed var(--border-color)' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 4 }}>
                <span style={{ fontWeight: 700, fontSize: 13, color: '#0f172a' }}>Operational metrics</span>
                <span style={{ fontSize: 11, color: '#94a3b8' }}>hours, counts and percentages, never part of the P&amp;L dollar totals</span>
              </div>
              <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 8, lineHeight: 1.5 }}>
                Hours come from payroll. Counts and percentages are typed in per month on each project's
                P&amp;L tab, under the Wages section. A percentage shows its average in the Total column;
                a count shows its sum.
              </div>

              {loadingOperational ? (
                <div style={{ padding: '8px 0' }}>
                  <Loader2 size={16} className="spinner" color="var(--color-primary)" />
                </div>
              ) : operationalMetrics.length > 0 ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
                  {operationalMetrics.map(m => {
                    const conf = operationalUnitDisplay(m.unit);
                    const Icon = conf.icon;
                    // The delete control is absent, not disabled, for anyone
                    // who could not use it: the registry's write policy is
                    // org admins only and excludes system rows, so for
                    // everyone else the button would exist only to fail.
                    // `canDeleteOperationalMetric` still re-checks both, as
                    // the guard of record.
                    const canOfferDelete = isAdmin && m.organization_id !== null && m.organization_id === organizationId;
                    const isChecking = checkingDelete.has(m.metric_key);
                    return (
                      <span
                        key={m.metric_key}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 5,
                          padding: '4px 5px 4px 9px', borderRadius: 999,
                          background: '#f0f7ff', border: '1px solid rgba(30,115,190,0.2)',
                          fontSize: 11.5, fontWeight: 600, color: '#1a5a9a',
                        }}
                        title={m.metric_key}
                      >
                        <Icon size={11} />
                        {m.display_name}
                        <span style={{ opacity: 0.6, fontWeight: 500 }}>· {conf.label.toLowerCase()}</span>
                        {canOfferDelete && (
                          <button
                            type="button"
                            className="btn btn-ghost"
                            onClick={() => { void handleRequestDeleteOperational(m); }}
                            disabled={isChecking}
                            style={{
                              padding: 2, marginLeft: 1, color: '#94a3b8', flexShrink: 0,
                              display: 'inline-flex', alignItems: 'center', borderRadius: 999,
                            }}
                            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = '#ef4444'; }}
                            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = '#94a3b8'; }}
                            aria-label={`Delete operational metric ${m.display_name}`}
                          >
                            {isChecking ? <Loader2 size={12} className="spinner" /> : <Trash2 size={12} />}
                          </button>
                        )}
                      </span>
                    );
                  })}
                </div>
              ) : (
                <div style={{ fontSize: 11.5, color: 'var(--text-secondary)', marginBottom: 10 }}>None yet.</div>
              )}

              <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <input
                  type="text"
                  className="form-input"
                  value={newOpName}
                  onChange={e => setNewOpName(e.target.value)}
                  placeholder="e.g. Overtime Hours"
                  aria-label="Operational metric name"
                  style={{ flex: 1, minWidth: 140, margin: 0, padding: '6px 10px', fontSize: 13 }}
                />
                {/* Design audit: "Unit" is new to this modal, so it gets a
                    visible label rather than aria-label alone. */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <label htmlFor="pnl-op-unit" style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Unit</label>
                  <select
                    id="pnl-op-unit"
                    className="form-input"
                    value={newOpUnit}
                    onChange={e => setNewOpUnit(e.target.value as OperationalUnit)}
                    style={{ margin: 0, padding: '5px 6px', fontSize: 11.5, width: 'auto' }}
                  >
                    <option value="hours">Hours</option>
                    <option value="count">Count</option>
                    <option value="percent">Percent</option>
                  </select>
                </div>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={handleAddOperationalMetric}
                  disabled={savingOperational || !newOpName.trim()}
                  style={{ display: 'flex', alignItems: 'center', gap: 5 }}
                >
                  {savingOperational ? <Loader2 size={13} className="spinner" /> : <Plus size={13} />}
                  Add
                </button>
              </div>
            </div>

            <div ref={listEndRef} />
          </div>
        )}

        {/* ── Fixed Bottom: Add + Summary + Actions ────────────────── */}
        <div style={{
          padding: '12px 20px 16px 20px', flexShrink: 0,
          borderTop: '1px solid var(--border-color)',
          background: 'var(--bg-secondary)',
        }}>
          {/* Add dropdown */}
          <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
            {GROUP_ORDER.map(g => {
              const gs = GROUP_STYLE[g];
              return (
                <button
                  key={g}
                  className="btn btn-ghost"
                  onClick={() => handleAdd(g)}
                  style={{
                    flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    gap: 4, padding: '7px 8px',
                    border: `1.5px dashed ${gs.border}`, borderRadius: 8,
                    fontSize: 11.5, fontWeight: 600, color: gs.color,
                    background: 'transparent',
                    cursor: 'pointer', transition: 'background 0.15s',
                  }}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = gs.bg; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                >
                  <Plus size={13} /> {gs.emoji} {g}
                </button>
              );
            })}
          </div>

          {/* Summary line */}
          <div style={{
            display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6,
            marginBottom: 12, fontSize: 11, color: '#64748b',
          }}>
            <span style={{ fontWeight: 600 }}>{localCategories.length} total</span>
            {GROUP_ORDER.map(g => grouped[g].length > 0 ? (
              <span key={g} style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                <span style={{ color: '#cbd5e1' }}>·</span>
                <span style={{ color: GROUP_STYLE[g].color, fontWeight: 600 }}>
                  {GROUP_STYLE[g].emoji} {grouped[g].length}
                </span>
              </span>
            ) : null)}
            {hasChanges && (
              <span style={{ color: '#f59e0b', fontWeight: 600, marginLeft: 'auto' }}>● Unsaved changes</span>
            )}
            {hasEmpty && (
              <span style={{ color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 3, marginLeft: hasChanges ? 0 : 'auto' }}>
                <AlertTriangle size={11} /> Empty names removed on save
              </span>
            )}
          </div>

          {/* Action buttons */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button className="btn btn-ghost" onClick={onClose} disabled={isSaving}>
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={isSaving || loading || hasDuplicates}
              style={{ display: 'flex', alignItems: 'center', gap: 6 }}
            >
              {isSaving ? <Loader2 size={14} className="spinner" /> : <Save size={14} />}
              {isSaving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>

      {/* Both dialogs are portalled to the body rather than nested in this
          one's DOM, so neither the editor's own scroll container nor its
          entry animation can clip or reposition them. The shared Modal's
          keyboard handling is stack aware, so Escape here closes only the
          dialog on top and leaves this editor and its unsaved edits alone. */}
      {createPortal(
        <Modal
          isOpen={pendingDelete !== null}
          onClose={() => setPendingDelete(null)}
          onConfirm={() => { void handleConfirmDeleteOperational(); }}
          title="Delete operational metric"
          confirmText={deletingMetric ? 'Deleting…' : 'Delete metric'}
          confirmDisabled={deletingMetric}
          size="sm"
        >
          <p style={{ color: 'var(--text-secondary)', lineHeight: 1.6, margin: 0 }}>
            Delete <strong>{pendingDelete?.display_name}</strong> from this organisation? No values have been
            typed under it, so nothing is lost, and it disappears from the P&amp;L tab and the analytics
            metric list. Any saved widget still pointing at it will say the metric no longer exists.
          </p>
        </Modal>,
        document.body,
      )}

      {createPortal(
        <Modal
          isOpen={deleteRefusal !== null}
          onClose={() => setDeleteRefusal(null)}
          title={deleteRefusal?.title ?? 'This metric cannot be deleted'}
          size="sm"
          hideFooter
        >
          <p style={{ color: 'var(--text-secondary)', lineHeight: 1.6, margin: '0 0 16px 0' }}>
            {deleteRefusal?.message}
          </p>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button className="btn btn-primary" onClick={() => setDeleteRefusal(null)}>
              Close
            </button>
          </div>
        </Modal>,
        document.body,
      )}
    </Modal>
  );
}
